<script lang="ts">
  import { socketService } from '../services/socketService';
  import { playerStore } from '../stores/playerStore';
  import { lobbyStore, setLobbyLoading } from '../stores/lobbyStore';
  import { getPlayerIdentity, savePlayerIdentity, clearPlayerIdentity } from '../lib/storage';
  import { announceToScreenReader } from '../lib/accessibility';
  import { JoinAction, type LobbyCheckResponse } from '@blather/shared';
  import { onMount } from 'svelte';

  let username = '';
  let lobbyId = '';
  let action: JoinAction | null = null;
  let checkError = '';
  let isChecking = false;
  let isSubmitting = false;

  // Reactive statement to check action when inputs change
  $: if (username.trim() && lobbyId.trim() && !isSubmitting) {
    checkActionDebounced();
  } else {
    action = null;
    checkError = '';
  }

  // Check for existing session on mount
  onMount(() => {
    const saved = getPlayerIdentity();
    if (saved.username && saved.lobbyId && saved.playerId) {
      username = saved.username;
      lobbyId = saved.lobbyId;
    }
  });

  // Debounce timer
  let checkTimer: ReturnType<typeof setTimeout> | null = null;

  function checkActionDebounced() {
    if (checkTimer) clearTimeout(checkTimer);
    checkTimer = setTimeout(checkAction, 300);
  }

  // Check what action should be taken
  function checkAction() {
    if (!username.trim() || !lobbyId.trim()) {
      checkError = 'Please enter both username and lobby ID';
      action = null;
      return;
    }

    isChecking = true;
    checkError = '';

    const socket = socketService.getSocket();
    if (!socket) {
      checkError = 'Not connected to server';
      isChecking = false;
      return;
    }

    socket.emit('lobby:check', { username: username.trim(), lobbyId: lobbyId.trim() }, (response: LobbyCheckResponse) => {
      isChecking = false;
      if (response.success) {
        action = response.action;
        announceToScreenReader(response.message);
      } else {
        checkError = response.message;
        action = null;
      }
    });
  }

  // Handle form submission
  function handleSubmit(event: Event) {
    event.preventDefault();

    if (!action) {
      checkAction();
      return;
    }

    isSubmitting = true;
    setLobbyLoading(true);
    const socket = socketService.getSocket();

    if (!socket) {
      checkError = 'Not connected to server';
      isSubmitting = false;
      setLobbyLoading(false);
      return;
    }

    const eventMap = {
      [JoinAction.CREATE]: 'lobby:create' as const,
      [JoinAction.JOIN]: 'lobby:join' as const,
      [JoinAction.JOIN_AUDIENCE]: 'lobby:joinAudience' as const,
      [JoinAction.RECONNECT]: 'lobby:reconnect' as const,
    };

    const eventName = eventMap[action];
    const saved = getPlayerIdentity();
    const payload = action === JoinAction.RECONNECT && saved.playerId
      ? { username: username.trim(), lobbyId: lobbyId.trim(), playerId: saved.playerId }
      : { username: username.trim(), lobbyId: lobbyId.trim() };

    socket.emit(eventName, payload as any, (response) => {
      isSubmitting = false;
      setLobbyLoading(false);

      if (response.success && response.player && response.lobby) {
        savePlayerIdentity(username.trim(), lobbyId.trim(), response.player.id);
        playerStore.set({
          player: response.player,
          username: username.trim(),
          lobbyId: lobbyId.trim(),
        });
        lobbyStore.update((s) => ({ ...s, lobby: response.lobby! }));
        announceToScreenReader(`Successfully ${getActionVerb(action)} lobby`);
      } else {
        // Handle Player ID mismatch error by clearing stale localStorage
        if (response.error?.includes('Player ID mismatch')) {
          clearPlayerIdentity();
          checkError = 'Session expired. Please try joining again.';
          action = null; // Reset to allow re-checking
          announceToScreenReader('Session expired. Please check the lobby again.', 'assertive');
        } else {
          checkError = response.error || `Failed to ${getActionVerb(action)} lobby`;
          announceToScreenReader(checkError, 'assertive');
        }
      }
    });
  }

  function getButtonText(action: JoinAction | null): string {
    if (!action) return 'Check Lobby';

    const textMap = {
      [JoinAction.CREATE]: 'Create Lobby',
      [JoinAction.JOIN]: 'Join Lobby',
      [JoinAction.JOIN_AUDIENCE]: 'Join as Audience',
      [JoinAction.RECONNECT]: 'Reconnect',
    };
    return textMap[action];
  }

  function getActionVerb(action: JoinAction | null): string {
    if (!action) return 'joined';

    const verbMap = {
      [JoinAction.CREATE]: 'created',
      [JoinAction.JOIN]: 'joined',
      [JoinAction.JOIN_AUDIENCE]: 'joined',
      [JoinAction.RECONNECT]: 'reconnected to',
    };
    return verbMap[action] || 'joined';
  }
</script>

<div class="lobby-entry" role="main">
  <h1>Join Blather Squared</h1>

  <form on:submit={handleSubmit} novalidate>
    <div class="form-group">
      <label for="username">Username</label>
      <input
        id="username"
        type="text"
        bind:value={username}
        required
        aria-required="true"
        aria-describedby="username-hint"
        disabled={isSubmitting}
        placeholder="Enter your display name"
      />
      <span id="username-hint" class="hint">Choose a display name for the game</span>
    </div>

    <div class="form-group">
      <label for="lobby-id">Lobby ID</label>
      <input
        id="lobby-id"
        type="text"
        bind:value={lobbyId}
        required
        aria-required="true"
        aria-describedby="lobby-hint"
        disabled={isSubmitting}
        placeholder="Enter or create a lobby name"
      />
      <span id="lobby-hint" class="hint">Enter an existing lobby name or create a new one</span>
    </div>

    {#if checkError}
      <div role="alert" class="error">{checkError}</div>
    {/if}

    <button
      type="submit"
      disabled={isChecking || isSubmitting || !username.trim() || !lobbyId.trim()}
      aria-busy={isChecking || isSubmitting}
    >
      {#if isChecking}
        Checking...
      {:else if isSubmitting}
        {getActionVerb(action || JoinAction.JOIN)}...
      {:else}
        {getButtonText(action)}
      {/if}
    </button>
  </form>

  <div class="footer-links">
    <a href="#/edit" class="editor-link">Content Editor</a>
  </div>
</div>

<style>
  .lobby-entry {
    max-width: 500px;
    margin: 2rem auto;
    padding: 2rem;
  }

  .footer-links {
    margin-top: 2rem;
    text-align: center;
    padding-top: 1.5rem;
    border-top: 1px solid var(--color-border, #ddd);
  }

  .editor-link {
    color: var(--color-primary, #007bff);
    text-decoration: none;
    font-weight: 500;
    font-size: 0.875rem;
  }

  .editor-link:hover {
    text-decoration: underline;
  }

  .editor-link:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .form-group {
    margin-bottom: 1.5rem;
  }

  button {
    width: 100%;
    font-size: 1.125rem;
    padding: 1rem;
  }

  @media (max-width: 600px) {
    .lobby-entry {
      padding: 1rem;
      margin: 1rem;
    }
  }
</style>
